// src/pages/business/BusinessLoginPage.tsx
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import BusinessApi from "../../services/BusinessApiService";

type LoginResponse = {
  success?: boolean;
  message?: string;
  code?: string;
  data?: {
    token?: string;
    refreshToken?: string;
    user?: any;
    businessUser?: any;
    business?: any;
  };
};

const hasFn = (obj: any, fn: string) => obj && typeof obj[fn] === "function";

/* ===============================
   Safe JWT decoder (unicode-safe)
   =============================== */
function decodeJwt<T = any>(token?: string | null): T | null {
  if (!token) return null;
  try {
    const parts = String(token).split(".");
    if (parts.length !== 3) return null;
    const json = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    const uriSafe = decodeURIComponent(
      json
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(uriSafe);
  } catch {
    return null;
  }
}

/* ===============================
   Defensive extraction of businessId
   =============================== */
function extractBusinessId(obj: any): string | undefined {
  if (!obj) return undefined;
  if (typeof obj === "string") return obj;
  if (typeof obj === "object") {
    if (typeof obj.businessId === "string") return obj.businessId;
    if (obj.businessId && typeof obj.businessId === "object") {
      return obj.businessId._id || obj.businessId.id || String(obj.businessId);
    }
    if (obj._id && typeof obj._id === "string") return obj._id;
    if (obj.id && typeof obj.id === "string") return obj.id;
  }
  return undefined;
}

const BusinessLoginPage: React.FC = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Forgot password modal
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMsg, setForgotMsg] = useState("");

  // If already logged in, go straight to stores
  useEffect(() => {
    try {
      if (hasFn(BusinessApi, "isLoggedIn") && BusinessApi.isLoggedIn()) {
        navigate("/business/stores", { replace: true });
      }
    } catch {
      /* ignore */
    }
  }, [navigate]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (error) setError(null);
  };

  /* ======================================================
     SINGLE SOURCE OF TRUTH for business context
     - Trust JWT claim first
     - Then user.businessId
     - Only persist a "business" blob if it MATCHES the claim
     ====================================================== */
  const setBusinessContext = (payload: any) => {
    const user = payload?.user || payload?.businessUser || {};
    const businessFromResp = payload?.business;

    const token =
      payload?.token ||
      (hasFn(BusinessApi, "getBusinessToken") && (BusinessApi as any).getBusinessToken()) ||
      localStorage.getItem("authToken") ||
      sessionStorage.getItem("authToken");

    const claims = decodeJwt<any>(token);

    // Defensive extraction of businessId
    let claimBizId: string | undefined = claims?.businessId;
    let chosenBizId: string | undefined = claimBizId;
    if (!chosenBizId && user?.businessId) {
      chosenBizId = extractBusinessId(user.businessId);
    }

    if (!chosenBizId) {
      console.warn("No businessId in JWT or user; NOT persisting demo business.");
    } else {
      localStorage.setItem("currentBusinessId", chosenBizId);
      localStorage.setItem(
        "businessData",
        JSON.stringify({
          businessId: chosenBizId,
          companyName: user?.companyName || businessFromResp?.companyName || "Unknown Company",
          email: user?.email,
        })
      );
    }

    // session basics
    localStorage.setItem("userType", "business_user");
    if (user?.email) localStorage.setItem("userEmail", user.email);
    localStorage.setItem("businessUser", JSON.stringify(user || null));

    // only keep a business blob if it matches the chosen biz id
    const businessIdFromResp = extractBusinessId(businessFromResp?.businessId);
    if (businessIdFromResp && chosenBizId && businessIdFromResp === chosenBizId) {
      localStorage.setItem("business", JSON.stringify(businessFromResp));
    } else {
      localStorage.removeItem("business");
    }
  };

  /* ======================================================
     Hydrate from canonical profile after login
     (server derives business from JWT -> reliable)
     ====================================================== */
  const postLoginHydrate = async (token?: string) => {
    try {
      if (hasFn(BusinessApi, "getProfile")) {
        // ✅ FIX: read shape returned by BusinessApi.getProfile()
        const res = await (BusinessApi as any).getProfile();
        if (res?.success && res.data) {
          const { user, business } = res.data;
          const claims = decodeJwt<any>(token);
          const claimId = claims?.businessId;
          setBusinessContext({
            token,
            user,
            business: claimId && extractBusinessId(business?.businessId) === claimId ? business : undefined,
          });
          return;
        }
      }
      // ✅ FIX: no double /api here
      const base = hasFn(BusinessApi, "getConfig") ? (BusinessApi as any).getConfig()?.baseURL : "";
      const res = await fetch(`${base}/business-user/profile`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const json = await res.json().catch(() => null);
      if (res.ok && json?.success && json.data) {
        const { user, business } = json.data;
        const claims = decodeJwt<any>(token);
        const claimId = claims?.businessId;
        setBusinessContext({
          token,
          user,
          business: claimId && extractBusinessId(business?.businessId) === claimId ? business : undefined,
        });
      }
    } catch {
      /* non-fatal */
    }
  };

  const mapError = (err: any): string => {
    const status = err?.status || err?.response?.status;
    const code = err?.code || err?.response?.data?.code;
    const msg = err?.message || err?.response?.data?.message;

    const byCode: Record<string, string> = {
      USER_NOT_FOUND: "Invalid email or password",
      INVALID_PASSWORD: "Invalid email or password",
      ACCOUNT_INACTIVE: "Your account is inactive. Please contact support.",
      BUSINESS_REJECTED: "Your business application was rejected. Please contact support.",
      BUSINESS_SUSPENDED: "Your business account is suspended. Please contact support.",
      MISSING_CREDENTIALS: "Please enter both email and password",
      LOGIN_FAILED: "Login failed. Please try again.",
      NETWORK_ERROR: "Network error. Please check your connection and try again.",
      RATE_LIMITED: "Too many attempts. Please try again in a minute.",
    };

    if (code && byCode[code]) return byCode[code];
    if (status === 401 || status === 403) return "Invalid email or password";
    if (status === 429) return "Too many attempts. Please try again in a minute.";
    if (typeof msg === "string" && msg.trim()) return msg;
    return "Network error. Please try again.";
  };

  const canSubmit = formData.email.trim() && formData.password;

  /* ======================================================
     HANDLE SUBMIT — actually calls setBusinessContext()
     ====================================================== */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) {
      setError("Please enter both email and password");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const resp: LoginResponse = hasFn(BusinessApi, "authLogin")
        ? await (BusinessApi as any).authLogin({
            email: formData.email.trim(),
            password: formData.password,
            rememberMe: formData.rememberMe,
          })
        : ({ success: false, message: "Auth service not available" } as any);

      if (!resp?.success) throw resp;

      const data = resp.data || {};

      // Persist tokens
      if (data.token) {
        if (hasFn(BusinessApi, "setBusinessToken")) {
          (BusinessApi as any).setBusinessToken(data.token);
        }
        (formData.rememberMe ? localStorage : sessionStorage).setItem("authToken", data.token);
      }
      if (data.refreshToken) {
        (formData.rememberMe ? localStorage : sessionStorage).setItem("refreshToken", data.refreshToken);
      }

      // Optional: persist user via service
      if (data.user && hasFn(BusinessApi, "setBusinessData")) {
        (BusinessApi as any).setBusinessData(data.user);
      }

      // Required by your ProtectedRoute logic
      localStorage.setItem("userType", "business_user");

      // 🔐 Set normalized business context (JWT-first)
      setBusinessContext(data);

      // 🔄 Hydrate from profile (server-derived)
      await postLoginHydrate(data.token);

      navigate("/business/stores", { replace: true });
    } catch (err: any) {
      setError(mapError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setForgotMsg("");

    try {
      if (!hasFn(BusinessApi, "forgotPassword")) throw new Error("Password reset service not available");
      const res = await (BusinessApi as any).forgotPassword(forgotEmail.trim());
      if (res?.success) {
        setForgotMsg(res.message || "Password reset email sent successfully.");
        setTimeout(() => {
          setShowForgot(false);
          setForgotEmail("");
          setForgotMsg("");
        }, 2500);
      } else {
        setForgotMsg(res?.message || "Failed to send reset email.");
      }
    } catch (e: any) {
      setForgotMsg(e?.message || "Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-700 to-indigo-800 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Header */}
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-2xl shadow-lg">
                JT
              </div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Business Portal</h2>
            <p className="text-gray-600">Access your JepTags business dashboard</p>
          </div>

          {/* Error */}
          {error && (
            <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                {error}
              </div>
            </div>
          )}

          {/* Login or Forgot Password */}
          {!showForgot ? (
            <form onSubmit={handleSubmit} className="mt-8 space-y-6" noValidate>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Business Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={onChange}
                  disabled={isLoading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="business@company.com"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={onChange}
                  disabled={isLoading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="••••••••••••"
                />
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center">
                  <input
                    id="rememberMe"
                    name="rememberMe"
                    type="checkbox"
                    checked={formData.rememberMe}
                    onChange={onChange}
                    disabled={isLoading}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:cursor-not-allowed"
                  />
                  <span className="ml-2 text-sm text-gray-900">Keep me signed in</span>
                </label>

                <button
                  type="button"
                  onClick={() => setShowForgot(true)}
                  disabled={isLoading}
                  className="text-sm font-medium text-blue-600 hover:text-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading || !canSubmit}
                className="w-full flex justify-center py-3 px-4 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                aria-busy={isLoading}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Signing in...
                  </>
                ) : (
                  "Sign in to Business Portal"
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleForgot} className="mt-8 space-y-6" noValidate>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Reset Password</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Enter your business email address and we'll send you a link to reset your password.
                </p>
                <label htmlFor="forgotEmail" className="block text-sm font-medium text-gray-700 mb-2">
                  Business Email Address
                </label>
                <input
                  id="forgotEmail"
                  type="email"
                  required
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  disabled={isLoading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="business@company.com"
                />
              </div>

              {forgotMsg && (
                <div
                  className={`p-3 rounded-lg text-sm ${
                    forgotMsg.toLowerCase().includes("sent") ||
                    forgotMsg.toLowerCase().includes("success")
                      ? "bg-green-50 text-green-800 border border-green-200"
                      : "bg-red-50 text-red-800 border border-red-200"
                  }`}
                >
                  <div className="flex items-center">
                    {forgotMsg.toLowerCase().includes("sent") ||
                    forgotMsg.toLowerCase().includes("success") ? (
                      <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 10-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    {forgotMsg}
                  </div>
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgot(false);
                    setForgotEmail("");
                    setForgotMsg("");
                  }}
                  disabled={isLoading}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Back to Login
                </button>
                <button
                  type="submit"
                  disabled={isLoading || !forgotEmail.trim()}
                  className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    "Send Reset Link"
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Footer Links */}
          <div className="mt-8 space-y-4 text-center">
            <div className="text-sm text-gray-600">
              Don&apos;t have a business account?{" "}
              <Link to="/business/register" className="font-medium text-blue-600 hover:text-blue-500 transition-colors">
                Register your business
              </Link>
            </div>
            <div className="text-sm text-gray-600">
              Looking for the consumer app?{" "}
              <Link to="/consumer/login" className="font-medium text-blue-600 hover:text-blue-500 transition-colors">
                Consumer login
              </Link>
            </div>
          </div>
        </div>

        {/* Dev-only Debug Panel */}
        {process.env.NODE_ENV === "development" && (
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-white text-xs">
            <h4 className="font-medium mb-2">🔧 Login Debug Info</h4>
            <div className="space-y-1">
              <div>Endpoint: POST /api/auth/business-user/login</div>
              <div>
                API Base:{" "}
                {hasFn(BusinessApi, "getConfig") ? (BusinessApi as any).getConfig().baseURL : "N/A"}
              </div>
              <div>
                Current User:{" "}
                {hasFn(BusinessApi, "isLoggedIn") && (BusinessApi as any).isLoggedIn() ? "Logged In" : "Not Logged In"}
              </div>
              <div>
                Auth Token:{" "}
                {hasFn(BusinessApi, "getBusinessToken") && (BusinessApi as any).getBusinessToken() ? "Present" : "Missing"}
              </div>
              <div>User Type: {localStorage.getItem("userType") || "None"}</div>
              <div>Business ID: {localStorage.getItem("currentBusinessId") || "None"}</div>
              <div>Business Data: {localStorage.getItem("businessData") ? "Present" : "Missing"}</div>

              <div className="flex flex-wrap gap-1 mt-2">
                <button
                  onClick={() => {
                    console.log("🔍 All localStorage");
                    for (let i = 0; i < localStorage.length; i++) {
                      const k = localStorage.key(i);
                      console.log(k, "=>", localStorage.getItem(k || ""));
                    }
                  }}
                  className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                >
                  Debug Auth
                </button>
                <button
                  onClick={() => {
                    localStorage.clear();
                    sessionStorage.clear();
                    window.location.reload();
                  }}
                  className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                >
                  Clear Storage
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessLoginPage;